import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { showErrorToast, showSuccessToast } from "utils/toast";

import { loadAccounts } from "app/store/account/slice/accountSlice";
import { useUpdateAccountPreferencesMutation } from "app/store/accountPreference/api/accountPreferenceApi";
import {
  showPreferenceList,
  toggleAccountFilter,
  toggleEditPreference,
} from "app/store/accountPreference/slice/accountPreferenceSlice";
import { ROLE_PREFERENCES } from "static/preferences";
import { sortObjectKeys } from "utils/accountPreference";

const useAccountPreferenceList = () => {
  const { accounts } = useSelector((state) => state.account);
  const { account, selectedAccount, toggleAccount } = useSelector(
    (state) => state.accountPreference
  );
  const dispatch = useDispatch();

  const [isEditPreference, setIsEditPreference] = useState(false);
  const [isCancelPreference, setIsCancelPreference] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const sortedRolePreferences = sortObjectKeys(
    account?.role_preferences,
    ROLE_PREFERENCES
  );

  const rolePreferences = { ...ROLE_PREFERENCES, ...sortedRolePreferences };

  const isRolePreference =
    rolePreferences && Object.keys(rolePreferences)?.length > 0 ? true : false;

  const [newAccount, setNewAccount] = useState("");
  const [preference, setPreference] = useState({});
  const [originalPreference, setOriginalPreference] = useState({});

  const [updateAccountPreferences] = useUpdateAccountPreferencesMutation();

  useEffect(() => {
    if (Object.keys(rolePreferences).length > 0) {
      setPreference({
        ...rolePreferences,
      });
      setOriginalPreference({
        ...rolePreferences,
      });
    }
    return () => {
      setPreference({});
      setOriginalPreference({});
    };
  }, [account]);

  useEffect(() => {
    if (!toggleAccount && !isEditPreference) {
      dispatch(
        showPreferenceList({
          showList: true,
          accounts,
          selectedAccount: newAccount !== "" ? newAccount : selectedAccount,
        })
      );
    }
  }, [dispatch, newAccount, toggleAccount, isEditPreference]);

  const editPreferences = () => {
    setIsEditPreference(true);
    dispatch(toggleEditPreference(true));
  };

  const changeRole = (featureKey, fieldKey, value) => {
    let roles = preference[featureKey][fieldKey];
    let accountRoles = [...roles];
    const roleIndex = roles.findIndex((role) => role === value);

    if (roleIndex >= 0) {
      accountRoles.splice(roleIndex, 1);
    } else {
      accountRoles.push(value);
    }

    const newPreference = {
      ...preference,
      [featureKey]: {
        ...preference[featureKey],
        [fieldKey]: accountRoles,
      },
    };

    setPreference({
      ...newPreference,
    });
  };

  const cancelEdit = () => {
    setPreference({ ...originalPreference });
    setIsEditPreference(false);
    dispatch(toggleEditPreference(false));
    setIsCancelPreference(false);
  };

  const savePreferences = async () => {
    setIsLoading(true);
    try {
      const payload = {
        account: selectedAccount,
        role_preferences: { ...preference },
      };

      const response = await updateAccountPreferences(payload).unwrap();

      if (!response?.error) {
        showSuccessToast(response?.message);
        setOriginalPreference({ ...preference });
        setIsLoading(false);
        setIsEditPreference(false);
        dispatch(toggleEditPreference(false));
        dispatch(loadAccounts(response?.data));
      }
    } catch (error) {
      showErrorToast(error?.message);
      setIsLoading(false);
    }
  };

  const changeAccountPreference = (event) => {
    setNewAccount(event.target.value);

    if (isEditPreference) {
      dispatch(toggleAccountFilter(true));
    }
  };

  const discardAccountToggle = () => {
    dispatch(toggleAccountFilter(false));
    dispatch(
      showPreferenceList({
        showList: true,
        accounts,
        selectedAccount: newAccount,
      })
    );
  };

  const cancelAccountToggle = () => {
    showPreferenceList({
      showList: true,
      accounts,
      selectedAccount,
    });
    dispatch(toggleAccountFilter(false));
  };

  return {
    dispatch,
    accounts,
    preference,
    isLoading,
    toggleAccount,

    changeRole,
    isCancelPreference,
    isEditPreference,
    isRolePreference,
    selectedAccount,

    cancelEdit,
    editPreferences,
    savePreferences,
    setIsCancelPreference,
    changeAccountPreference,
    discardAccountToggle,
    cancelAccountToggle,
  };
};

export default useAccountPreferenceList;
